import type { Image } from './types';

/* The image filters the pages build their filter bars from.

   They live in the domain rather than in the viewer because they are a property of the data,
   not of the component that draws it - the routes filter the image list before handing it
   over, and the viewer never reads them. */

interface FilterTag {
    title: string;
    tag: string;
    filter: (image: Image) => boolean;
}

function hasTag(image: Image, tag: string) {
    return image.bboxes?.some((box) => box.tag === tag) ?? false;
}

/** A measurement the image lacks never matches, as `undefined < 20` did not. */
function matchesMeasurement(
    value: number | null | undefined,
    test: (value: number) => boolean
): boolean {
    return typeof value === 'number' && test(value);
}

const WOUND_TAGS = ['wound', 'healing_wound'];
const LICE_TAGS = ['adult_female_lice', 'movable', 'skottelus'];

function generateLaksvelFilterTags(includeScores: number[]): FilterTag[] {
    return [
        {
            title: 'molecules.fishHealthFilter.all',
            tag: 'all',
            filter: (image) =>
                includeScores.some(
                    (score) => Math.round(image?.indicators?.body_wounds_score ?? 0) === score
                ),
        },
        ...includeScores.map((score) => ({
            title: `scores.score_${score}`,
            tag: `laksvel_${score}`,
            filter: (image: Image) =>
                Math.round(image?.indicators?.body_wounds_score ?? 0) === score,
        })),
    ];
}

export const LAKSVEL_FILTER_TAGS = generateLaksvelFilterTags([1, 2, 3]);

export const FILTER_TAGS: FilterTag[] = [
    {
        title: 'molecules.fishHealthFilter.all',
        tag: 'all',
        filter: (image) => WOUND_TAGS.some((tag) => hasTag(image, tag)),
    },
    {
        title: 'molecules.fishHealthFilter.active',
        tag: 'wound',
        filter: (image) => hasTag(image, 'wound'),
    },
    {
        title: 'molecules.fishHealthFilter.healing',
        tag: 'healing_wound',
        filter: (image) => hasTag(image, 'healing_wound'),
    },
    {
        title: 'molecules.fishHealthFilter.small',
        tag: 'small',
        filter: (image) =>
            image.bboxes?.some(
                (box) =>
                    matchesMeasurement(box.size_x_mm, (mm) => mm < 20) ||
                    matchesMeasurement(box.size_y_mm, (mm) => mm < 20)
            ) ?? false,
    },
    {
        title: 'molecules.fishHealthFilter.medium',
        tag: 'medium',
        filter: (image) =>
            image.bboxes?.some(
                (box) =>
                    matchesMeasurement(box.size_x_mm, (mm) => mm >= 20 && mm < 50) ||
                    matchesMeasurement(box.size_y_mm, (mm) => mm >= 20 && mm < 50)
            ) ?? false,
    },
    {
        title: 'molecules.fishHealthFilter.large',
        tag: 'large',
        filter: (image) =>
            image.bboxes?.some(
                (box) =>
                    matchesMeasurement(box.size_x_mm, (mm) => mm >= 50) ||
                    matchesMeasurement(box.size_y_mm, (mm) => mm >= 50)
            ) ?? false,
    },
    {
        title: 'imageFilters.mature',
        tag: 'maturation_tag',
        filter: (image) =>
            matchesMeasurement(image.indicators?.maturity, (maturity) => maturity >= 0.5),
    },
];

export const LICE_FILTER_TAGS: FilterTag[] = [
    {
        title: 'molecules.fishHealthFilter.all',
        tag: 'all',
        filter: (image) => LICE_TAGS.some((tag) => hasTag(image, tag)),
    },
    {
        title: 'molecules.fishHealthFilter.adultFemale',
        tag: 'adult_female_lice',
        filter: (image) => hasTag(image, 'adult_female_lice'),
    },
    {
        title: 'molecules.fishHealthFilter.movable',
        tag: 'movable',
        filter: (image) => hasTag(image, 'movable'),
    },
    {
        title: 'molecules.fishHealthFilter.caligus',
        tag: 'skottelus',
        filter: (image) => hasTag(image, 'skottelus'),
    },
];
